import { shouldBlockIndexing } from './utils/is-preprod-host';

export function isPreProd(): boolean {
  return shouldBlockIndexing({
    hostname: window.location.hostname,
    noIndexFlag: import.meta.env.VITE_NO_INDEX,
  });
}

const ADSENSE_SRC =
  'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1270273366659592';

const TITLE_SUFFIX = ' (PREPROD)';
const BANNER_ID = 'preprod-banner';

function ensurePreProdTitle(): void {
  if (!document.title.endsWith(TITLE_SUFFIX)) {
    document.title = `${document.title.replace(/\s*\(PREPROD\)$/, '')}${TITLE_SUFFIX}`;
  }
}

function watchTitle(): void {
  ensurePreProdTitle();
  const titleEl = document.querySelector('title');
  if (titleEl) {
    new MutationObserver(ensurePreProdTitle).observe(titleEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }
  // Other scripts may assign document.title after load (e.g. monthly printer SEO)
  document.addEventListener('DOMContentLoaded', ensurePreProdTitle);
  window.setTimeout(ensurePreProdTitle, 0);
}

function injectPreProdBanner(): void {
  if (document.getElementById(BANNER_ID)) return;

  const style = document.createElement('style');
  style.textContent = `
    #${BANNER_ID} {
      position: sticky;
      top: 0;
      z-index: 9999;
      background: #1a1a1a;
      color: #f5d76e;
      font: 700 0.75rem/1.2 ui-monospace, "Cascadia Code", "Segoe UI Mono", monospace;
      letter-spacing: 0.08em;
      text-align: center;
      padding: 0.35rem 0.75rem;
    }
    @media print {
      #${BANNER_ID} { display: none !important; }
    }
  `;
  document.head.appendChild(style);

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'status');
  banner.textContent = 'PREPROD — not the live site';

  const mount = () => {
    document.body.prepend(banner);
  };
  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', mount);
  }
}

/**
 * Blocks indexing on pre-prod: robots meta, drop canonical.
 * Loads AdSense only when not pre-prod.
 * Gated by shouldBlockIndexing — no effect on calendarify.online even if merged to main.
 */
export function applyPreProdGuards(): void {
  if (isPreProd()) {
    const robotsContent = 'noindex, nofollow, noarchive';

    document.querySelectorAll('meta[name="robots"], meta[name="googlebot"]').forEach((el) => {
      el.setAttribute('content', robotsContent);
    });

    if (!document.querySelector('meta[name="robots"]')) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = robotsContent;
      document.head.appendChild(meta);
    }

    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    watchTitle();
    injectPreProdBanner();
    return;
  }

  if (!document.querySelector(`script[src*="adsbygoogle"]`)) {
    const s = document.createElement('script');
    s.async = true;
    s.src = ADSENSE_SRC;
    s.crossOrigin = 'anonymous';
    document.head.appendChild(s);
  }
}

applyPreProdGuards();
